var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { uploadV1 } from '../client/UploadV1';
import { getImageFormat, isRaster } from './utility';
function createGreyScale(imgData) {
    const pixels = imgData.data;
    for (let i = 0; i < pixels.length; i += 4) {
        const lightness = Math.floor((pixels[i] + pixels[i + 1] + pixels[i + 2]) / 3);
        pixels[i] = lightness;
        pixels[i + 1] = lightness;
        pixels[i + 2] = lightness;
    }
    return imgData;
}
function calculateLineartness(histogramArr) {
    const histogram = histogramArr;
    const l = histogram.length;
    let normalizationValue = 0;
    for (let i = 0; i < l; i++) {
        normalizationValue += histogram[i];
    }
    for (let i = 0; i < l; i++) {
        histogram[i] /= normalizationValue;
    }
    const histogramFirst = [];
    for (let i = 0; i < l; i++) {
        histogramFirst[i] = 0;
    }
    for (let i = 1; i < l - 1; i++) {
        if (histogram[i + 1] !== 0) {
            histogramFirst[i] = histogram[i] / histogram[i + 1];
            if (histogramFirst[i] < 0) {
                histogramFirst[i] = 0;
            }
        }
    }
    normalizationValue = 0;
    for (let i = 1; i < l; i++) {
        normalizationValue += histogram[i];
    }
    const lineartnessLow = histogram[0] / normalizationValue;
    const d = normalizationValue * 0.8;
    let sum = 0;
    let radius = 0;
    for (let i = 1; i < l; i++) {
        sum += histogram[i];
        if (sum >= d) {
            radius = i;
            break;
        }
    }
    let lineartnessHigh = 0;
    for (let i = 2 * radius; i < l; i++) {
        const r2 = i - 2 * radius;
        lineartnessHigh += histogram[i] * r2;
    }
    let lineartnessMiddle = 0;
    for (let i = 2; i < radius - 1; i++) {
        const v = (histogramFirst[i - 1] + histogramFirst[i + 1]) / 2;
        const delta = v - histogramFirst[i];
        lineartnessMiddle += delta * delta;
    }
    const result = lineartnessLow + lineartnessMiddle + lineartnessHigh;
    return Number.isFinite(result) ? result : 10000;
}
const MAX_IMAGE_SIZE = 1; // in MB
const MAX_IMAGE_DIMENSION = 1024;
export function shouldGenerateImagePreview(file) {
    const maxAllowedSize = MAX_IMAGE_SIZE * 1024 * 1024; // in bytes
    return file.size > maxAllowedSize;
}
export function generateImagePreview(file, headers) {
    if (!file) {
        throw new Error(`File is empty`);
    }
    if (!isRaster(file)) {
        throw new Error(`file is not supported`);
    }
    return new Promise((resolve) => {
        try {
            const img = document.createElement('img');
            const blobUrl = URL.createObjectURL(file);
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            img.onload = () => __awaiter(this, void 0, void 0, function* () {
                try {
                    let scalingFactor;
                    if (img.height > img.width) {
                        scalingFactor = MAX_IMAGE_DIMENSION / img.height;
                    }
                    else {
                        scalingFactor = MAX_IMAGE_DIMENSION / img.width;
                    }
                    const previewHeight = img.height * scalingFactor;
                    const previewWidth = img.width * scalingFactor;
                    canvas.height = previewHeight;
                    canvas.width = previewWidth;
                    if (!ctx)
                        return;
                    ctx.drawImage(img, 0, 0, previewWidth, previewHeight);
                    const imgData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
                    ctx.putImageData(imgData, 0, 0);
                    yield canvas.toBlob((blob) => __awaiter(this, void 0, void 0, function* () {
                        if (blob) {
                            const uploadV1Response = yield uploadV1({
                                headers,
                                file: blob,
                            });
                            resolve(uploadV1Response.data);
                            return;
                        }
                        Promise.reject(new Error('some error ocurred'));
                    }), 'image/webp');
                }
                catch (exception) {
                    throw new Error('Exception while processing imageInfo on Image Load');
                }
            });
            img.onerror = () => {
                throw new Error('Error while fetching image');
            };
            img.src = blobUrl;
        }
        catch (exception) {
            throw new Error('Exception while processing imageInfo');
        }
    });
}
export function getImageInfoByClassification(file) {
    if (!file) {
        throw new Error(`File is empty`);
    }
    if (!isRaster(file)) {
        throw new Error(`file is not supported`);
    }
    return new Promise((resolve) => {
        try {
            let originalImageHeight;
            let originalImageWidth;
            let isLogo;
            let isPhoto;
            const img = document.createElement('img');
            const blobUrl = URL.createObjectURL(file);
            const width = 100;
            const height = 100;
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            let result;
            img.onload = () => {
                try {
                    originalImageHeight = img.height;
                    originalImageWidth = img.width;
                    canvas.height = height;
                    canvas.width = width;
                    img.height = height;
                    img.width = width;
                    if (!ctx)
                        return;
                    ctx.drawImage(img, 0, 0, width, height);
                    let imgData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
                    imgData = createGreyScale(imgData);
                    ctx.putImageData(imgData, 0, 0);
                    const histogram = [];
                    for (let i = 0; i < 256; i++) {
                        histogram[i] = 0;
                    }
                    for (let y = 0; y < imgData.height; y++) {
                        for (let x = 0; x < imgData.width - 1; x++) {
                            const i = (imgData.width * y + x) * 4;
                            // RGB values are all the same, and alpha is always 100%
                            const lum = imgData.data[i];
                            const lum2 = imgData.data[i + 4];
                            const diff = Math.abs(lum2 - lum);
                            histogram[diff] += 1;
                        }
                    }
                    const lineartScore = calculateLineartness(histogram);
                    if (lineartScore <= 6) {
                        isLogo = false;
                        isPhoto = true;
                    }
                    else {
                        isLogo = true;
                        isPhoto = false;
                    }
                    result = {
                        isLogo,
                        isPhoto,
                        lineartness: lineartScore,
                        height: originalImageHeight,
                        width: originalImageWidth,
                        format: getImageFormat(file.type),
                        isError: false,
                        isSupported: false,
                    };
                    resolve(result);
                }
                catch (exception) {
                    throw new Error('Exception while processing imageInfo on Image Load');
                }
            };
            img.onerror = (error) => {
                throw new Error(`Error while fetching image: ${error}`);
            };
            img.src = blobUrl;
        }
        catch (exception) {
            throw new Error('Exception while processing imageInfo');
        }
    });
}
//# sourceMappingURL=imageClassification.js.map